import React, { FC, useEffect, useMemo, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MuiButton from "@mui/material/Button";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { useCreateRole, useUpdateRole } from "@/hooks/useRoles";
import { useUserStore } from "@/hooks/useUserStore";
import { useActions } from "@/hooks/useActions";
import { RoleType } from "@/domain/rol.interface";
import { IAction } from "@/domain/actions.interface";

export type RoleFormData = {
  name: string;
  type: string;
  subType: string;
  status: string;
  actions: string[];
};

type RoleModalProps = {
  open: boolean;
  onClose: () => void;
  role?: RoleType | null;
};

const defaultFormData: RoleFormData = {
  name: "",
  type: "",
  subType: "",
  status: "ACTIVE",
  actions: [],
};

const RoleModal: FC<RoleModalProps> = ({ open, onClose, role }) => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const currentUserId = userInfo?._id;

  const isEditMode = Boolean(role?._id);

  const createRole = useCreateRole();
  const updateRole = useUpdateRole();

  const isPending = createRole.isPending || updateRole.isPending;

  const [formData, setFormData] = useState<RoleFormData>(defaultFormData);
  const [errors, setErrors] = useState<
    Partial<Record<keyof RoleFormData, string>>
  >({});

  const { actions: actionsData, isLoadingActions } = useActions(
    {
      pageNumber: 0,
      pageSize: 500,
      sortField: "name",
      sortOrder: 1,
    },
    open
  );

  const availableActions: IAction[] = actionsData?.list || [];

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name || "",
        type: role.type || "",
        subType: role.subType || "",
        status: role.status || "ACTIVE",
        actions: Array.isArray(role.actions) ? role.actions : [],
      });
    } else {
      setFormData(defaultFormData);
    }
    setErrors({});
  }, [role, open]);

  const statusOptions = useMemo(
    () => [
      { key: "ACTIVE", value: "Active" },
      { key: "INACTIVE", value: "Inactive" },
    ],
    []
  );

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof RoleFormData, string>> = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      name: formData.name.trim(),
      type: formData.type.trim(),
      subType: formData.subType.trim(),
      status: formData.status,
      actions: formData.actions,
    };

    try {
      if (isEditMode && role?._id) {
        await updateRole.mutateAsync(
          {
            id: role._id,
            data: { ...payload, updatedBy: currentUserId },
          },
          {
            onSuccess: () => {
              showSnackBar("The role has been updated.", "success");
              onClose();
            },
            onError: (error: ErrorResponse) => {
              showSnackBar(
                (Array.isArray(error.message)
                  ? error.message[0]
                  : error.message) ||
                  error.error ||
                  "An error occurred"
              );
            },
          }
        );
      } else {
        await createRole.mutateAsync(
          { ...payload, createdBy: currentUserId },
          {
            onSuccess: () => {
              showSnackBar("The role has been created.", "success");
              onClose();
            },
            onError: (error: ErrorResponse) => {
              showSnackBar(
                (Array.isArray(error.message)
                  ? error.message[0]
                  : error.message) ||
                  error.error ||
                  "An error occurred"
              );
            },
          }
        );
      }
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEditMode ? "Edit role" : "Add role"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 440,
        maxHeight: 600,
        maxWidth: 560,
      }}
      content={
        <Box sx={{ width: "100%", minHeight: 380, padding: "20px 0" }}>
          <Grid container spacing={"20px"}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                error={!!errors.name}
                helperText={errors.name || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Type"
                value={formData.type}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, type: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sub Type"
                value={formData.subType}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, subType: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Select
                handleChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: value as string,
                  }))
                }
                data={statusOptions}
                value={formData.status}
                label="Status"
                id="role-status"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Box sx={{ fontSize: 14, color: "text.secondary" }}>
                  {formData.actions.length} of {availableActions.length}{" "}
                  selected
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <MuiButton
                    size="small"
                    variant="outlined"
                    disabled={
                      isPending ||
                      isLoadingActions ||
                      availableActions.length === 0 ||
                      formData.actions.length === availableActions.length
                    }
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        actions: availableActions.map((a) => a._id),
                      }))
                    }
                  >
                    Select all
                  </MuiButton>
                  <MuiButton
                    size="small"
                    variant="text"
                    disabled={isPending || formData.actions.length === 0}
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, actions: [] }))
                    }
                  >
                    Clear
                  </MuiButton>
                </Box>
              </Box>
              <Autocomplete
                multiple
                options={availableActions}
                loading={isLoadingActions}
                getOptionLabel={(option) => option.name || option._id || ""}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                value={availableActions.filter((a) =>
                  formData.actions.includes(a._id)
                )}
                onChange={(_e, selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    actions: selected.map((a) => a._id),
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Actions"
                    placeholder="Select actions"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={handleSubmit}
            disabled={isPending}
            isLoading={isPending}
            label={isEditMode ? "SAVE" : "CREATE"}
          />
        </Box>
      }
    />
  );
};

export default RoleModal;
